import styles from "./task-card.module.css";

import { useMemo, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import routes from "@/routes";

import {
  Card,
  Tag,
  Flex,
  Typography,
  Button,
  Avatar,
  Popover,
  ConfigProvider,
  Checkbox,
} from "antd";

import ArrowUpIcon from "@/icons/ArrowUpIcon";
import PlusSquareIcon from "@/icons/PlusSquareIcon";
import ExclamationCircleIcon from "@/icons/ExclamationCircleIcon";
import EmployeeProfile from "@/components/cards/EmployeeProfile";
import ContextMenu from "@/components/containers/ContextMenu";

import { formateISODateToStrMin, getName } from "@/utils";
import {
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  EXECUTOR_EDIT_ROLES,
  START_DATE_EDIT_ROLES,
  DUE_DATE_EDIT_ROLES,
  TASK_TYPE_OPTIONS,
  isTaskStatusFinal,
} from "@/constants/tasks";

const { Paragraph } = Typography;

// Карточка задачи содержит основную информацию о задаче
// Используется на странице задач
const TaskCard = ({
  task,
  statuses = [],
  priorities = [],
  taskTypes = [],
  onEdit,
  onDelete,
  onCopyItem,
  onPublishItem,
  isAdmins = false,
  unit = null,
  isExpandCards = true,
  showActions = true,
  selectable = false,
  selected = false,
  onSelect,
}) => {
  const template = task?.template ?? null;

  const statusUuid = task?.status_uuid;
  const importanceUuid = task?.importance_uuid ?? template?.importance_uuid;
  const taskTypeUuid = task?.task_type_uuid ?? template?.task_type_uuid;
  const parentTaskUuid = task?.parent_task_uuid ?? template?.parent_task_uuid;

  const parent = task?.parent ?? template?.parent ?? null;
  const author = task?.author ?? template?.author ?? null;
  const executor = task?.executor ?? template?.executor ?? null;
  const unitData = task?.unit ?? template?.unit ?? null;
  const description = task?.description ?? template?.description ?? "";

  const isPlannedTask = !!task.scheduled_due_date;

  const navigate = useNavigate();

  const contextMenuRef = useRef(null);

  const [isExpand, setIsExpand] = useState(isExpandCards);

  const status = isPlannedTask
    ? { color: "gray", name: "Планируется" }
    : statuses.find((status) => status.uuid === statusUuid);
  const priority = priorities.find(
    (priority) => priority.uuid === importanceUuid
  );
  const taskType = taskTypes.find((type) => type.uuid === taskTypeUuid);

  const isDeleted = useMemo(() => {
    const delStatus = statuses.find(({ is_for_deletion }) => is_for_deletion);
    return statusUuid === delStatus?.uuid;
  }, [statuses, statusUuid]);

  const isEnded = isTaskStatusFinal(status || task?.status);

  const isDraft = useMemo(() => {
    const draftStatus = statuses.find(({ is_draft }) => is_draft);
    return statusUuid === draftStatus?.uuid;
  }, [statuses, statusUuid]);

  const isSubTask = parentTaskUuid && parent?.task_number;

  const authorName = `${author?.lastname[0] || ""}${
    author?.firstname[0] || ""
  }`;
  const getPriorityIcon = (color) => {
    return PRIORITY_OPTIONS.find((item) => item.value === color)?.icon;
  };
  const getStatusIcon = (color) => {
    return STATUS_OPTIONS.find((item) => item.value === color)?.icon;
  };

  const getTypeIcon = (color) => {
    return TASK_TYPE_OPTIONS.find((item) => item.value === color)?.icon;
  };

  const is_overdue =
    (task.due_date || task.scheduled_due_date) &&
    new Date() >= new Date(task.due_date || task.scheduled_due_date);

  const authorPopover = (
    <Flex align="center" gap={12} style={{ minWidth: 224 }}>
      <Avatar
        size={36}
        src={author?.avatar}
        style={{
          color: "#E66A25",
          background: "#FFEADE",
          fontWeight: 500,
          border: "none",
          textTransform: "uppercase",
        }}
      >
        {authorName}
      </Avatar>
      <Flex vertical gap={4}>
        <div style={{ color: "#777777" }}>Автор заявки</div>
        <div style={{ color: "#303030" }}>
          {getName(author?.lastname, author?.firstname, author?.patronymic)}
        </div>
      </Flex>
    </Flex>
  );
  const taskPopover = (
    <Flex vertical gap={4} style={{ minWidth: 224 }}>
      <div style={{ color: "#777777" }}>Задача {parent?.task_number}</div>
      <div style={{ color: "#303030", maxWidth: 300, fontWeight: 500 }}>
        {parent?.name}
      </div>
    </Flex>
  );

  const taskHref = useMemo(() => {
    return isAdmins
      ? routes.administration.taskDetail(task.uuid)
      : unit
      ? routes.unitTaskDetail(task.unit_uuid, task.uuid)
      : routes.taskDetail(task.uuid);
  }, [task, unit, isAdmins]);

  const isUserCanEditExecutor = useMemo(() => {
    if (!task.roles) return false;
    if (isPlannedTask) return false;
    return (
      showActions &&
      !isDeleted &&
      !isEnded &&
      !task.readOnly &&
      task.roles.some((role) => EXECUTOR_EDIT_ROLES.includes(role))
    );
  }, [task, isDeleted, isEnded, showActions, isPlannedTask]);

  const isUserCanEditStartDate = useMemo(() => {
    if (!task.roles) return false;
    if (isPlannedTask) return false;
    return (
      showActions &&
      !isDeleted &&
      !isEnded &&
      !task.readOnly &&
      task.roles.some((role) => START_DATE_EDIT_ROLES.includes(role))
    );
  }, [task, isDeleted, isEnded, showActions, isPlannedTask]);

  const isUserCanEditDueDate = useMemo(() => {
    if (!task.roles) return false;
    if (isPlannedTask) return false;
    return (
      showActions &&
      !isDeleted &&
      !isEnded &&
      !task.readOnly &&
      task.roles.some((role) => DUE_DATE_EDIT_ROLES.includes(role))
    );
  }, [task, isDeleted, isEnded, showActions, isPlannedTask]);

  const showExecutor = useMemo(() => {
    return executor || (isUserCanEditExecutor && showActions);
  }, [isUserCanEditExecutor, showActions, executor]);

  useEffect(() => {
    setIsExpand(isExpandCards);
  }, [isExpandCards]);

  const handleClick = (e) => {
    if (
      (contextMenuRef.current && contextMenuRef.current.contains(e.target)) ||
      e.target.closest(".ant-dropdown") ||
      e.target.closest("button") ||
      e.target.closest("input")
    )
      return;

    setIsExpand(!isExpand);
  };

  const handleClickCapture = (e) => {
    if (!selectable) return;

    if (
      (contextMenuRef.current && contextMenuRef.current.contains(e.target)) ||
      e.target.closest(".ant-dropdown") ||
      e.target.closest("button") ||
      e.target.closest("input")
    )
      return;

    e.preventDefault();
    e.stopPropagation();
    onSelect?.(task.uuid, !selected);
  };

  const handleSelectFromMenu = onSelect
    ? () => onSelect(task.uuid, !selected)
    : null;
  const selectItemText = selected ? "Отменить выделение" : "Выделить";

  return (
    <ConfigProvider
      theme={{
        components: {
          Card: {
            headerHeight: 55,
            headerPadding: 20,
            bodyPadding: "0 20px 20px",
          },
        },
      }}
    >
      <Card
        hoverable={!isPlannedTask}
        title={
          <Flex
            align="center"
            wrap
            gap={8}
            className={styles.header}
            style={{ fontSize: 12, fontWeight: 400, color: "#777777" }}
          >
            {selectable && (
              <Checkbox
                checked={selected}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onSelect?.(task.uuid, e.target.checked)}
              />
            )}

            <Popover content={authorPopover} placement="rightTop" arrow={false}>
              <Avatar
                size={24}
                src={author?.avatar}
                style={{
                  color: "#E66A25",
                  background: "#FFEADE",
                  fontWeight: 500,
                  zIndex: 1,
                  border: "none",
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                {authorName}
              </Avatar>
            </Popover>

            <Flex align="center" gap={3}>
              {taskType && (
                <Popover
                  content={
                    <div style={{ margin: "-5px 0" }}>{taskType.name}</div>
                  }
                  placement="rightTop"
                  arrow={false}
                >
                  <div style={{ zIndex: 1, fontSize: 16 }}>
                    {getTypeIcon(taskType.key)}
                  </div>
                </Popover>
              )}

              <span>
                {isSubTask ? "Подзадача" : "Задача"}{" "}
                {task.task_number || template?.template_number}
              </span>
            </Flex>

            {isSubTask && (
              <Popover content={taskPopover} placement="rightTop" arrow={false}>
                <Link
                  className="blue-link"
                  to={
                    isAdmins
                      ? routes.administration.taskDetail(parentTaskUuid)
                      : unit
                      ? routes.unitTaskDetail(parent?.unit_uuid, parentTaskUuid)
                      : routes.taskDetail(parentTaskUuid)
                  }
                  style={{ zIndex: 1 }}
                >
                  <ArrowUpIcon />
                  {parent?.task_number}
                </Link>
              </Popover>
            )}

            {priority && (
              <Tag
                icon={getPriorityIcon(priority.color)}
                className="tag-with-icon"
              >
                {priority.name}
              </Tag>
            )}
          </Flex>
        }
        extra={
          showActions &&
          (task.readOnly ? (
            <ContextMenu
              ref={contextMenuRef}
              vertical
              onCopy={() => onCopyItem(task)}
              onView={() => navigate(taskHref)}
              onSelectItem={handleSelectFromMenu}
              selectItemText={selectItemText}
            />
          ) : isDeleted || isEnded ? (
            <ContextMenu
              ref={contextMenuRef}
              vertical
              onCopy={() => onCopyItem(task)}
              onView={() => navigate(taskHref)}
              onSelectItem={handleSelectFromMenu}
              selectItemText={selectItemText}
            />
          ) : isDraft ? (
            <ContextMenu
              ref={contextMenuRef}
              vertical
              onCopy={() => onCopyItem(task)}
              onView={() => navigate(taskHref)}
              onEdit={() => onEdit(task)}
              onDelete={() => onDelete(task)}
              onPublish={() => onPublishItem(task)}
              onSelectItem={handleSelectFromMenu}
              selectItemText={selectItemText}
            />
          ) : (
            <ContextMenu
              ref={contextMenuRef}
              vertical
              onCopy={() => onCopyItem(task)}
              onView={() => navigate(taskHref)}
              onEdit={() => onEdit(task)}
              onDelete={() => onDelete(task)}
              onSelectItem={handleSelectFromMenu}
              selectItemText={selectItemText}
            />
          ))
        }
        className={styles.card}
        onClickCapture={handleClickCapture}
        onClick={handleClick}
      >
        <Flex vertical gap={15} className={styles.body}>
          <Paragraph ellipsis={{ rows: 2 }} className={styles.title}>
            {!isPlannedTask ? (
              <Link to={taskHref}>{task.name}</Link>
            ) : (
              <div>{template?.name}</div>
            )}
          </Paragraph>

          {description && (
            <Paragraph
              ellipsis={{ rows: 3 }}
              className={styles.description}
              title={description}
            >
              {description}
            </Paragraph>
          )}

          <Flex
            vertical
            gap={15}
            flex={1}
            className={isExpand ? "" : styles.collapse}
          >
            {(showExecutor || unitData) && (
              <Flex gap={15} align="start">
                {showExecutor && (
                  <Flex vertical gap={4} align="start" flex={1}>
                    <div style={{ color: "#BBBBBB" }}>Исполнитель</div>
                    {!!executor ? (
                      <EmployeeProfile
                        {...executor}
                        fullName={false}
                        avatarSize={24}
                        color="#777777"
                      />
                    ) : (
                      !isPlannedTask && (
                        <Button
                          color="default"
                          variant="link"
                          size="small"
                          type="text"
                          icon={<PlusSquareIcon />}
                          onClick={() => onEdit(task)}
                          style={{ paddingInline: 0 }}
                        >
                          Назначить
                        </Button>
                      )
                    )}
                  </Flex>
                )}

                {unitData && (
                  <Flex vertical gap={4} align="start" flex={1}>
                    <div style={{ color: "#BBBBBB" }}>Рабочая группа</div>
                    <div style={{ color: "#777777" }}>
                      {unitData?.name || "-"}
                    </div>
                  </Flex>
                )}
              </Flex>
            )}

            <Flex
              align="center"
              justify="space-between"
              wrap
              gap={8}
              style={{ marginTop: "auto" }}
            >
              {!task.start_date &&
              !task.scheduled_start_date &&
              !task.due_date &&
              !task.scheduled_due_date ? (
                isUserCanEditStartDate ? (
                  <Button
                    color="default"
                    variant="link"
                    size="small"
                    type="text"
                    icon={<PlusSquareIcon />}
                    onClick={() => onEdit(task)}
                    style={{ paddingInline: 0 }}
                  >
                    Установить сроки
                  </Button>
                ) : (
                  <span></span>
                )
              ) : (
                <Flex align="center" gap={4} style={{ color: "#777777" }}>
                  {!!task.start_date || !!task.scheduled_start_date ? (
                    <span>
                      {formateISODateToStrMin(
                        task.start_date || task.scheduled_start_date
                      )}
                    </span>
                  ) : isUserCanEditStartDate ? (
                    <Button
                      color="default"
                      variant="link"
                      size="small"
                      type="text"
                      icon={<PlusSquareIcon />}
                      onClick={() => onEdit(task)}
                      style={{ paddingInline: 0 }}
                    ></Button>
                  ) : (
                    "Не назначена"
                  )}
                  <span style={{ color: "#BBBBBB" }}>—</span>
                  {!!task.due_date || !!task.scheduled_due_date ? (
                    <Flex align="center" gap={4}>
                      {is_overdue && (
                        <ExclamationCircleIcon
                          style={{ fontSize: 14, color: "#E66A25" }}
                        />
                      )}
                      <span>
                        {formateISODateToStrMin(
                          task.due_date || task.scheduled_due_date
                        )}
                      </span>
                    </Flex>
                  ) : isUserCanEditDueDate ? (
                    <Button
                      color="default"
                      variant="link"
                      size="small"
                      type="text"
                      icon={<PlusSquareIcon />}
                      onClick={() => onEdit(task)}
                      style={{ paddingInline: 0 }}
                    ></Button>
                  ) : (
                    "Не назначена"
                  )}
                </Flex>
              )}

              {status && (
                <Tag
                  icon={getStatusIcon(status.color)}
                  color={isPlannedTask ? "" : status.color}
                  className="tag-with-icon-color"
                >
                  {status.name}
                </Tag>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Card>
    </ConfigProvider>
  );
};

export default TaskCard;
